import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Euro,
  ShoppingCart,
  BarChart3,
  Clock,
  Layers,
  ExternalLink,
  Eye,
  Globe,
  Users,
  UserX,
  MousePointerClick,
  LogOut,
  Search,
  Download,
  ChevronDown,
  ChevronUp,
  Route,
  RotateCcw,
  Gift,
  ChevronRight,
  CreditCard,
} from 'lucide-react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import apiService from '../services/api';
import {
  ActiviteByTypePanel,
  M1_LABEL_FALLBACK,
} from './AnalyticsRegistreBanner';
import AnalyticsPeriodSelector from './analytics/AnalyticsPeriodSelector';
import AnalyticsClickableKpiCard from './analytics/AnalyticsClickableKpiCard';
import AnalyticsDetailView from './analytics/AnalyticsDetailView';
import AnalyticsInfoHint from './analytics/AnalyticsInfoHint';
import { buildAnalyticsDetailPageModel, PAGE_TYPE_LABELS } from './analytics/buildAnalyticsDetailPageModel';
import { kpiHelp } from './analytics/analyticsKpiDefinitions';
import { DeltaBadge } from './analytics/analyticsDelta';
import { mapCanauxMetierUiLabels, SITE_WEB_SEMANTICS_HINT } from '../utils/canalMetierLabels';
import { CHART_COLORS } from '../utils/analyticsDataMapper';

ChartJS.register(ArcElement, Tooltip, Legend);

const STATUT_COLORS = {
  EN_ATTENTE: 'bg-yellow-500',
  CONFIRMEE: 'bg-orange-500',
  EN_PREPARATION: 'bg-orange-500',
  PRETE: 'bg-purple-500',
  EN_LIVRAISON: 'bg-indigo-500',
  LIVREE: 'bg-green-500',
  ANNULEE: 'bg-red-500',
};

const CommercantAnalytics = ({ commercantId, initialOverview = null, moduleCaisse = false }) => {
  const [data, setData] = useState(initialOverview);
  const [loading, setLoading] = useState(!initialOverview);
  const [error, setError] = useState('');
  const [periode, setPeriode] = useState('30j');
  const [customDateDebut, setCustomDateDebut] = useState('');
  const [customDateFin, setCustomDateFin] = useState('');
  const [expandedSession, setExpandedSession] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [detailKey, setDetailKey] = useState(null);
  const [productSort, setProductSort] = useState('ca'); // 'ca' | 'quantite'

  const openOverviewStyleDetail = useCallback(
    (metric) => {
      // Ne pas router vers Vue d’ensemble : son détail est MTD mois,
      // alors qu’Analytics conserve 7j/30j/90j/1a/custom. Même pattern UX, période Analytics.
      setDetailKey(metric);
    },
    []
  );

  const fetchAnalytics = useCallback(async () => {
    if (!commercantId) return;
    const useCustom = periode === 'custom';
    if (useCustom && (!customDateDebut || !customDateFin)) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      if (!apiService.isAuthenticated()) {
        apiService.logoutHard();
        return;
      }
      const query = useCustom
        ? { dateDebut: customDateDebut, dateFin: customDateFin }
        : { period: periode };
      if (
        !useCustom &&
        periode === '30j' &&
        initialOverview?.schemaVersion &&
        initialOverview?.kpi?.volume
      ) {
        setData({
          ...initialOverview,
          canaux: mapCanauxMetierUiLabels(initialOverview.canaux || []),
        });
        return;
      }
      const overview = await apiService.getCommercantAnalyticsOverview(commercantId, query);
      setData({
        ...overview,
        canaux: mapCanauxMetierUiLabels(overview.canaux || []),
      });
    } catch (err) {
      console.error('Erreur fetchAnalytics:', err);
      setError("Impossible de charger les analytics. Réessayez.");
    } finally {
      setLoading(false);
    }
  }, [commercantId, periode, customDateDebut, customDateFin, initialOverview]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    if (initialOverview) {
      setData({
        ...initialOverview,
        canaux: mapCanauxMetierUiLabels(initialOverview.canaux || []),
      });
    }
  }, [initialOverview]);

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      const exportQuery =
        periode === 'custom' && customDateDebut && customDateFin
          ? { dateDebut: customDateDebut, dateFin: customDateFin }
          : { period: periode };
      await apiService.downloadCommercantComportementCsv(commercantId, exportQuery);
    } catch (err) {
      console.error('Export CSV:', err);
      alert('Impossible de télécharger l\'export CSV.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  const kpi = data?.kpi || {};
  const resolver = data?.resolver || {};
  const activite = data?.activite || {};
  const m1Label =
    resolver.m1Label || kpi.volume?.label || M1_LABEL_FALLBACK;
  const volumeValue = kpi.volume?.value ?? 0;
  const volumeDelta = kpi.volume?.deltaPct;
  const trafic = data?.trafic || {};
  const comportement = data?.comportement || {};
  const visiteurs = comportement.visiteurs || {};
  const funnel = comportement.funnel || {};
  const fmt = (n) => Number(n || 0).toFixed(2);
  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  const detailModel = detailKey
    ? buildAnalyticsDetailPageModel({
        detailKey,
        mode: 'merchant',
        overview: { ...data, commercantId },
        periodLabel:
          data?.periode?.debutLocal && data?.periode?.finLocal
            ? `${data.periode.debutLocal} → ${data.periode.finLocal}`
            : periode,
      })
    : null;

  if (detailKey && detailModel) {
    return (
      <AnalyticsDetailView
        model={detailModel}
        onBack={() => setDetailKey(null)}
        evolutionChart={null}
        performanceOverview={data}
        detailKey={detailKey}
        merchantName={data?.commercant?.nom || data?.commercantNom || null}
        periodControls={
          <AnalyticsPeriodSelector
            periode={periode}
            onPeriodChange={setPeriode}
            customDateDebut={customDateDebut}
            customDateFin={customDateFin}
            onCustomDebutChange={setCustomDateDebut}
            onCustomFinChange={setCustomDateFin}
            tone="blue"
            testId="analytics-detail-period-selector"
          />
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <div>{error}</div>
          <button type="button" onClick={fetchAnalytics} className="text-sm font-medium underline">
            Réessayer
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics</h2>
          {data?.periode && (
            <p className="text-sm text-gray-500 mt-0.5">
              {data.periode.debutLocal} → {data.periode.finLocal}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <AnalyticsPeriodSelector
            periode={periode}
            onPeriodChange={setPeriode}
            customDateDebut={customDateDebut}
            customDateFin={customDateFin}
            onCustomDebutChange={setCustomDateDebut}
            onCustomFinChange={setCustomDateFin}
            tone="blue"
          />
          {moduleCaisse === true && (
            <Link
              to={`/caisse/kpi?commercantId=${commercantId}`}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium bg-slate-800 text-white hover:bg-slate-700"
            >
              KPI Caisse (POS)
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>

      {/* KPI critiques — hiérarchie métier */}
      <section aria-label="KPI principaux" data-testid="analytics-merchant-kpi-row">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <AnalyticsClickableKpiCard
            data-testid="analytics-kpi-ca"
            title="CA net ventes"
            helpText={kpiHelp('caNetVentes')}
            value={fmt(kpi.ca?.value)}
            suffix=" €"
            subText={
              kpi.ca?.brut > 0 || kpi.ca?.rembourse > 0
                ? `Brut ${fmt(kpi.ca?.brut)} € − Remboursé ${fmt(kpi.ca?.rembourse)} €`
                : undefined
            }
            deltaPct={kpi.ca?.deltaPct}
            icon={Euro}
            tone="green"
            footerHint="Comprendre le CA net"
            onOpen={() => openOverviewStyleDetail('ca')}
          />
          <AnalyticsClickableKpiCard
            data-testid="analytics-kpi-commandes"
            title={m1Label || 'Commandes payées'}
            helpText={kpiHelp('commandesPayees')}
            value={volumeValue}
            deltaPct={volumeDelta}
            icon={ShoppingCart}
            tone="blue"
            footerHint="Volume payé & statuts"
            onOpen={() => openOverviewStyleDetail('commandes')}
          />
          <AnalyticsClickableKpiCard
            data-testid="analytics-kpi-panier"
            title="Panier moyen ventes"
            helpText={kpiHelp('panierMoyen')}
            value={fmt(kpi.panierMoyen?.value)}
            suffix=" €"
            deltaPct={kpi.panierMoyen?.deltaPct}
            icon={BarChart3}
            tone="purple"
            footerHint="Comprendre le panier moyen"
            onOpen={() => setDetailKey('panier')}
          />
          <AnalyticsClickableKpiCard
            data-testid="analytics-kpi-remboursements"
            title="Remboursements"
            helpText={kpiHelp('remboursements')}
            value={fmt(kpi.ca?.rembourse)}
            suffix=" €"
            subText={
              kpi.ca?.rembourse > 0 ? 'Déduits du CA net sur la période' : 'Aucun remboursement'
            }
            icon={RotateCcw}
            tone="orange"
            footerHint="Identifier l’impact remboursements"
            onOpen={() => setDetailKey('remboursements')}
          />
        </div>
        <p className="mt-2 text-[11px] text-gray-500">
          Les pages détail Analytics reprennent le langage Vue d’ensemble (hero, performance,
          sous-KPI) tout en conservant la période sélectionnée ci-dessus — pas le mois MTD Overview.
        </p>
      </section>

      <ActiviteByTypePanel activite={activite} resolver={resolver} />

      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Globe className="h-5 w-5 text-sky-600" />
              Trafic site boutique
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Visites de votre page en ligne (sessions, heures de passage, pages vues)
            </p>
          </div>
          <button
            type="button"
            data-testid="analytics-trafic-detail-btn"
            onClick={() => setDetailKey('trafic')}
            className="inline-flex items-center gap-1 text-sm font-medium text-sky-700 hover:text-sky-900"
          >
            Voir le détail
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {!trafic.trackingActif ? (
          <div className="rounded-lg bg-sky-50 border border-sky-100 px-4 py-3 text-sm text-sky-900">
            Aucune visite enregistrée sur cette période. Le compteur démarre dès qu&apos;un client
            ouvre votre boutique sur le site Clickorico.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  Pages vues
                  <AnalyticsInfoHint text={kpiHelp('pagesVues')} />
                </p>
                <p className="text-2xl font-bold text-gray-900">{trafic.totalVues ?? 0}</p>
                <DeltaBadge value={trafic.deltaVuesPct} />
              </div>
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  Visites
                  <AnalyticsInfoHint text={kpiHelp('sessions')} />
                </p>
                <p className="text-2xl font-bold text-gray-900">{trafic.sessionsUniques ?? 0}</p>
                <DeltaBadge
                  value={trafic.deltaSessionsPct}
                  periodHint={
                    data?.periode?.debutLocal
                      ? `Période actuelle : ${data.periode.debutLocal} → ${data.periode.finLocal}. Comparée à la période précédente de même durée.`
                      : undefined
                  }
                />
              </div>
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  Pages vues par visite
                  <AnalyticsInfoHint text={kpiHelp('pagesParSession')} />
                </p>
                <p className="text-2xl font-bold text-gray-900">{trafic.vuesParVisite ?? 0}</p>
              </div>
            </div>
            {/* Conversion : secondaire */}
            <div
              className="mb-6 rounded-lg border border-amber-100 bg-amber-50/70 px-4 py-3"
              data-testid="analytics-conversion-secondary"
            >
              <p className="text-xs font-medium text-amber-950 flex items-center gap-1.5">
                Taux de transformation en achat
                <AnalyticsInfoHint text={kpiHelp('conversion')} />
              </p>
              <p className="text-lg font-semibold text-amber-950 mt-0.5">
                {trafic.conversionVisiteCommande != null
                  ? `${trafic.conversionVisiteCommande}%`
                  : '—'}
              </p>
              <p className="text-[11px] text-amber-900/80 mt-1">
                Part des visites ayant abouti à un achat suivi par le site. Inclut les achats de
                test site ; hors caisse.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                  <Eye className="h-4 w-4" />
                  Évolution des visites
                </h4>
                <div
                  className="max-h-48 overflow-y-auto overflow-x-hidden"
                  data-testid="analytics-dashboard-trafic-evolution"
                >
                  <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(4.5rem,auto)_minmax(5rem,auto)] gap-x-3 gap-y-2 text-sm items-baseline">
                    {(trafic.evolution || []).map((jour) => (
                      <div key={jour.date} className="contents">
                        <span className="text-gray-600 min-w-0 truncate">
                          {new Date(`${jour.date}T12:00:00`).toLocaleDateString('fr-FR', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                          })}
                        </span>
                        <span className="font-medium text-right tabular-nums whitespace-nowrap">
                          {jour.vues} vues
                        </span>
                        <span className="text-gray-500 text-right tabular-nums whitespace-nowrap">
                          {jour.sessions} visites
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  Heures de passage sur le site
                </h4>
                <div className="space-y-2">
                  {(trafic.heuresPics || []).length === 0 ? (
                    <p className="text-sm text-gray-500">Pas assez de données.</p>
                  ) : (
                    (trafic.heuresPics || []).map((h) => (
                      <div key={h.heure} className="flex justify-between text-sm">
                        <span className="text-gray-600">
                          {h.heure}h – {h.heure + 1}h
                        </span>
                        <span className="font-medium">{h.vues} vues</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {(trafic.parTypePage || []).length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                  Pages consultées par type
                  <AnalyticsInfoHint text="Nombre de pages vues, regroupées par type de page (boutique, fiche produit…)." />
                </p>
                <div className="flex flex-wrap gap-2">
                  {trafic.parTypePage.map((t) => (
                    <span
                      key={t.pageType}
                      className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 inline-flex items-center gap-1"
                    >
                      {PAGE_TYPE_LABELS[t.pageType] || `${t.pageType} (vues)`}
                      {' · '}
                      {t.vues} vues
                      {t.pageType === 'MERCHANT' ? (
                        <AnalyticsInfoHint text={kpiHelp('pageTypeBoutique')} />
                      ) : t.pageType === 'PRODUCT' ? (
                        <AnalyticsInfoHint text={kpiHelp('pageTypeProduit')} />
                      ) : null}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {(() => {
              const knownGeo = (trafic.parPays || []).filter(
                (g) => g.pays && !/inconnu|unknown/i.test(String(g.pays))
              );
              if (!knownGeo.length) return null;
              return (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                  Origine géographique des pages vues
                  <AnalyticsInfoHint text={kpiHelp('geoVisites')} />
                </p>
                <p className="text-[11px] text-gray-400 mb-2">Pays · Pages vues</p>
                <div className="flex flex-wrap gap-2">
                  {knownGeo.map((g) => (
                    <span
                      key={g.pays}
                      className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700"
                    >
                      {g.pays} · {g.pagesVues ?? g.visites} pages vues
                    </span>
                  ))}
                </div>
              </div>
              );
            })()}
          </>
        )}
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <MousePointerClick className="h-5 w-5 text-violet-600" />
              Comportement des visiteurs sur le site
              <AnalyticsInfoHint text="Parcours d’achat, abandons, recherches et visiteurs identifiés sur votre boutique en ligne." />
            </h3>
            <p className="text-xs text-gray-500">
              Visiteur sans compte vs visiteur identifié, parcours d&apos;achat, abandons et produits
              consultés
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <button
              type="button"
              onClick={() => setDetailKey('comportement')}
              className="inline-flex items-center gap-1 text-sm font-medium text-violet-700 hover:text-violet-900"
            >
              Voir le détail
              <ChevronRight className="h-4 w-4" />
            </button>
          {comportement.trackingActif && (
            <div className="flex flex-col items-end gap-1">
              <button
                type="button"
                onClick={handleExportCsv}
                disabled={exporting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                {exporting ? 'Export…' : 'Exporter le parcours site (CSV)'}
              </button>
              <p className="text-[11px] text-gray-400 max-w-xs text-right flex items-center gap-1 justify-end">
                <AnalyticsInfoHint text={kpiHelp('csvComportement')} />
                Visiteurs, funnel, recherches et sessions de la période
              </p>
            </div>
          )}
          </div>
        </div>

        {!comportement.trackingActif ? (
          <div className="rounded-lg bg-violet-50 border border-violet-100 px-4 py-3 text-sm text-violet-900">
            Les données comportementales s&apos;accumulent dès qu&apos;un client navigue sur votre
            boutique (consultation produit, panier, checkout). Rechargez après quelques visites test.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <UserX className="h-3.5 w-3.5" /> Visiteurs sans compte
                  <AnalyticsInfoHint text={kpiHelp('visiteursAnonymes')} />
                </p>
                <p className="text-2xl font-bold text-gray-900">{visiteurs.anonymes ?? 0}</p>
                <p className="text-[11px] text-gray-400">sessions anonymes</p>
              </div>
              <div className="rounded-lg bg-slate-50 p-4">
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" /> Visiteurs identifiés
                  <AnalyticsInfoHint text={kpiHelp('visiteursIdentifies')} />
                </p>
                <p className="text-2xl font-bold text-gray-900">{visiteurs.connectes ?? 0}</p>
                <p className="text-[11px] text-gray-400">
                  {visiteurs.pctConnectes ?? 0}% des visites · visiteurs identifiés (tous rôles)
                </p>
                <button
                  type="button"
                  className="mt-2 text-xs font-medium text-violet-700 hover:underline"
                  onClick={() => setDetailKey('comportement')}
                >
                  Voir qui · détail →
                </button>
              </div>
              <div className="rounded-lg bg-slate-50 p-4 col-span-2">
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  Parcours d&apos;achat
                  <AnalyticsInfoHint text="Nombre de sessions atteignant chaque étape. Le % indique le taux d’abandon entre deux étapes successives." />
                </p>
                <div className="mt-2 space-y-1.5">
                  {(funnel.etapes || []).map((etape, i) => {
                    const prev = i > 0 ? funnel.etapes[i - 1]?.sessions : etape.sessions;
                    const drop =
                      i > 0 && prev > 0
                        ? Math.round(((prev - etape.sessions) / prev) * 100)
                        : null;
                    const pass =
                      i > 0 && prev > 0
                        ? Math.round((etape.sessions / prev) * 100)
                        : null;
                    return (
                      <div key={etape.key} className="flex justify-between text-sm gap-2">
                        <span className="text-gray-700">{etape.label}</span>
                        <span className="font-medium text-right shrink-0">
                          {etape.sessions} sessions
                          {pass != null ? (
                            <span className="text-gray-500 text-xs ml-1.5 font-normal">
                              · {pass}% de passage
                            </span>
                          ) : null}
                          {drop != null && drop > 0 ? (
                            <span className="text-amber-700 text-xs ml-1.5 font-normal">
                              · {drop}% d’abandon
                            </span>
                          ) : null}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {(comportement.pointsAbandon || []).length > 0 && (
              <div className="mb-6 rounded-lg border border-amber-100 bg-amber-50/50 p-4">
                <h4 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-1.5">
                  <LogOut className="h-4 w-4" />
                  Où les visiteurs quittent le parcours
                  <AnalyticsInfoHint text="Sessions qui s’arrêtent à cette étape sans poursuivre. Inclut les visiteurs sans compte." />
                </h4>
                <div className="space-y-2">
                  {comportement.pointsAbandon.map((p) => (
                    <div key={p.etape} className="flex justify-between text-sm gap-2">
                      <span className="text-amber-900">{p.label}</span>
                      <span className="font-semibold text-amber-800 shrink-0">
                        {p.sessions} sessions
                        {p.pct != null ? ` · ${p.pct}%` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {comportement.correlationClients?.clientsVisiteursConnectes > 0 && (
              <div className="mb-6 rounded-lg border border-emerald-100 bg-emerald-50/40 p-4">
                <h4 className="text-sm font-semibold text-emerald-900 mb-2">
                  Corrélation visite → achat (visiteurs identifiés)
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs">Clients visiteurs</p>
                    <p className="font-bold text-gray-900">
                      {comportement.correlationClients.clientsVisiteursConnectes}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Visite puis achat</p>
                    <p className="font-bold text-emerald-700">
                      {comportement.correlationClients.clientsVisitePuisAchat}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Visite sans achat</p>
                    <p className="font-bold text-amber-700">
                      {comportement.correlationClients.clientsVisiteSansAchat}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">Taux conversion</p>
                    <p className="font-bold text-gray-900">
                      {comportement.correlationClients.tauxConversionVisiteAchat}%
                    </p>
                  </div>
                </div>
              </div>
            )}

            {(comportement.topRecherches || []).length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-1.5">
                  <Search className="h-4 w-4" />
                  Recherches dans le menu
                  <AnalyticsInfoHint text="Termes saisis dans la recherche de votre boutique pendant la période." />
                </h4>
                <div className="overflow-x-auto rounded-lg border border-gray-100">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs text-gray-500">
                      <tr>
                        <th className="px-3 py-2 font-medium">Recherche</th>
                        <th className="px-3 py-2 font-medium text-right">Recherches</th>
                        <th className="px-3 py-2 font-medium text-right">Sessions</th>
                        <th className="px-3 py-2 font-medium text-right">Résultats moy.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {comportement.topRecherches.map((r) => (
                        <tr key={r.query}>
                          <td className="px-3 py-2 text-gray-800">« {r.query} »</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.recherches}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{r.sessions}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {r.resultatsMoyens != null ? r.resultatsMoyens : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Produits les plus consultés</h4>
                {(comportement.topProduitsConsultes || []).length === 0 ? (
                  <p className="text-sm text-gray-500">Pas encore de consultation produit.</p>
                ) : (
                  (() => {
                    const rows = comportement.topProduitsConsultes;
                    const showCat = rows.some((p) => p.categorie);
                    return (
                  <div className="overflow-x-auto rounded-lg border border-gray-100">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-left text-xs text-gray-500">
                        <tr>
                          <th className="px-3 py-2 font-medium">Produit</th>
                          {showCat ? <th className="px-3 py-2 font-medium">Catégorie</th> : null}
                          <th className="px-3 py-2 font-medium text-right">Vues</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {rows.map((p, i) => (
                          <tr key={p.produitId || i}>
                            <td className="px-3 py-2 text-gray-800">{p.nom}</td>
                            {showCat ? (
                              <td className="px-3 py-2 text-gray-600">{p.categorie || '—'}</td>
                            ) : null}
                            <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                              {p.vues} vues
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                    );
                  })()
                )}
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2">
                  Visiteurs identifiés les plus actifs
                </h4>
                {(comportement.topClientsConnectes || []).length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Aucun visiteur identifié sur la période (visiteurs sans compte uniquement).
                  </p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {comportement.topClientsConnectes.map((c) => {
                      const isClient =
                        c.estClientBoutique === true ||
                        Number(c.nbCommandes || 0) > 0 ||
                        Boolean(c.carteFidelite);
                      return (
                      <div
                        key={c.userId}
                        className="rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2"
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="min-w-0 flex-1">
                            {isClient ? (
                              <Link
                                to={`/clients/${c.userId}?commercantId=${commercantId}`}
                                className="text-sm font-semibold text-violet-700 hover:text-violet-900 hover:underline inline-flex items-center gap-1 flex-wrap"
                              >
                                {c.prenom} {c.nom}
                                {c.carteFidelite && (
                                  <span className="text-[10px] font-medium text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded no-underline">
                                    fidélité
                                  </span>
                                )}
                              </Link>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setDetailKey('comportement')}
                                className="text-sm font-semibold text-gray-900 hover:text-violet-800 inline-flex items-center gap-1 flex-wrap text-left"
                              >
                                {c.prenom} {c.nom}
                                <span className="text-[10px] font-medium text-slate-600 bg-slate-200 px-1.5 py-0.5 rounded">
                                  visiteur
                                </span>
                              </button>
                            )}
                            <p className="text-xs text-gray-500 truncate">
                              {c.email || '—'}
                            </p>
                            <p className="text-xs text-gray-500">
                              {c.visites} visite{c.visites > 1 ? 's' : ''} · {c.pagesVues} pages
                              vues
                            </p>
                          </div>
                          <span className="text-[11px] text-gray-400 shrink-0">
                            {fmtDate(c.derniereVisite)}
                          </span>
                        </div>
                        <p className="text-xs text-violet-700 mt-1">
                          Dernière action : {c.dernierEventLabel || '—'}
                          {c.dernierProduit ? ` · ${c.dernierProduit}` : ''}
                        </p>
                        {isClient ? (
                          <Link
                            to={`/clients/${c.userId}?commercantId=${commercantId}&tab=comportement`}
                            className="inline-block mt-1.5 text-[11px] font-medium text-violet-600 hover:underline"
                          >
                            Voir la fiche client →
                          </Link>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setDetailKey('comportement')}
                            className="inline-block mt-1.5 text-[11px] font-medium text-violet-600 hover:underline"
                          >
                            Détail visiteur Analytics →
                          </button>
                        )}
                        {c.nbCommandes != null && (
                          <p className="text-[11px] text-gray-400 mt-0.5">
                            {c.nbCommandes} commande{c.nbCommandes > 1 ? 's' : ''} chez vous
                            {c.pointsFidelite != null ? ` · ${c.pointsFidelite} pts fidélité` : ''}
                          </p>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {(comportement.sessionsRecentes || []).length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-1.5">
                  <Route className="h-4 w-4" />
                  Parcours récents (session par session)
                </h4>
                <div className="space-y-2">
                  {comportement.sessionsRecentes.map((s) => {
                    const open = expandedSession === s.sessionId;
                    const clientLabel = s.client
                      ? `${s.client.prenom} ${s.client.nom}`
                      : 'Visiteur anonyme';
                    return (
                      <div
                        key={s.sessionId}
                        className="rounded-lg border border-gray-100 overflow-hidden"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedSession(open ? null : s.sessionId)
                          }
                          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-gray-50 hover:bg-gray-100 text-left"
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {clientLabel}
                              {s.converti && (
                                <span className="ml-2 text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                                  converti
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              {fmtDate(s.debut)} · {s.dureeMinutes} min · {s.nbActions}{' '}
                              actions
                            </p>
                          </div>
                          {open ? (
                            <ChevronUp className="h-4 w-4 shrink-0 text-gray-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
                          )}
                        </button>
                        {open && (
                          <div className="px-3 py-2 space-y-1 bg-white border-t border-gray-100">
                            {(s.actions || []).map((a, i) => (
                              <div
                                key={`${s.sessionId}-${i}`}
                                className="flex justify-between gap-2 text-xs"
                              >
                                <span className="text-gray-700">{a.label}</span>
                                <span className="text-gray-400 shrink-0">
                                  {new Date(a.at).toLocaleTimeString('fr-FR', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    second: '2-digit',
                                  })}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
        <p className="text-[11px] text-gray-400 mt-4">{data?.meta?.comportement}</p>
      </div>

      {/* Gift — bas de page, compact, détail dans Analytics */}
      {data?.gift ? (
        <section
          aria-label="Cartes cadeaux"
          data-testid="analytics-gift-section"
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-5"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Gift className="h-4 w-4 text-pink-600" aria-hidden />
              <h3 className="text-base font-semibold text-gray-900">
                Cartes cadeaux (hors CA ventes)
              </h3>
            </div>
            <button
              type="button"
              data-testid="analytics-kpi-gift"
              onClick={() => setDetailKey('gift')}
              className="inline-flex items-center gap-1 text-sm font-medium text-pink-700 hover:text-pink-900"
            >
              Voir le détail
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => setDetailKey('gift')}
            className="w-full text-left space-y-3 rounded-lg border border-transparent hover:border-pink-100 hover:bg-pink-50/40 p-1 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-300"
            aria-label="Ouvrir le détail cartes cadeaux"
          >
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
                Activité pendant la période
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <p className="text-[11px] text-gray-500 flex items-center gap-1">
                    Cartes vendues
                    <AnalyticsInfoHint text={kpiHelp('giftVendu')} />
                  </p>
                  <p className="text-lg font-bold text-gray-900">{data.gift.soldCount ?? 0}</p>
                  <p className="text-[11px] text-gray-500">
                    {fmt(data.gift.soldAmount)} € de valeur vendue
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500 flex items-center gap-1">
                    Montant utilisé pendant la période
                    <AnalyticsInfoHint text="Toutes cartes confondues — y compris vendues avant la période." />
                  </p>
                  <p className="text-sm font-semibold text-gray-900">{fmt(data.gift.usedAmount)} €</p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500">Remboursé / annulé</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {fmt(data.gift.refundedAmount)} €
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500 flex items-center gap-1">
                    Taux d’utilisation des cartes vendues
                    <AnalyticsInfoHint text={kpiHelp('giftTaux')} />
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {data.gift.utilizationRate != null
                      ? `${Math.round(data.gift.utilizationRate * 100)} %`
                      : '—'}
                  </p>
                </div>
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2">
                Situation à fin de période
                {data.gift.asOf
                  ? ` (${new Date(data.gift.asOf).toLocaleDateString('fr-FR')})`
                  : ''}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-3">
                <div>
                  <p className="text-[11px] text-gray-500 flex items-center gap-1">
                    Cartes actives / encours
                    <AnalyticsInfoHint text={kpiHelp('giftEncours')} />
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {data.gift.activeCount ?? 0}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    {fmt(data.gift.outstandingAmount)} € d’encours
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500">Expiration sous 30 jours</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {data.gift.expiring30d?.count ?? 0} · {fmt(data.gift.expiring30d?.amount)} €
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500">Expiration sous 7 jours</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {data.gift.expiring7d?.count ?? 0} · {fmt(data.gift.expiring7d?.amount)} €
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500">Expirées avec solde</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {data.gift.expiredWithBalance?.count ?? 0} ·{' '}
                    {fmt(data.gift.expiredWithBalance?.amount)} €
                  </p>
                </div>
              </div>
            </div>
          </button>
        </section>
      ) : null}

      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Layers className="h-5 w-5 text-slate-500" />
            Performance par canal
            <AnalyticsInfoHint text="Répartition du CA et des commandes payées selon Click & Collect, livraison, caisse…" />
          </h3>
          <button
            type="button"
            onClick={() => setDetailKey('commandes')}
            className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:text-slate-900"
          >
            Analyser les canaux
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
          {(data?.canaux || []).length === 0 ? (
            <p className="text-sm text-gray-500">Aucune vente sur la période.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Canal</th>
                    <th className="px-3 py-2 font-medium text-right">Commandes</th>
                    <th className="px-3 py-2 font-medium text-right">CA</th>
                    <th className="px-3 py-2 font-medium text-right">Panier moyen</th>
                    <th className="px-3 py-2 font-medium text-right">Part du CA</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.canaux.map((c) => {
                    const cmd = Number(c.commandes || 0);
                    const ca = Number(c.ca || 0);
                    const totalCa = Number(kpi.ca?.value || 0);
                    const part =
                      totalCa > 0 ? Math.round((ca / totalCa) * 1000) / 10 : null;
                    return (
                      <tr key={c.key}>
                        <td className="px-3 py-2 font-medium text-gray-800">{c.label}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{cmd}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmt(ca)} €</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {cmd > 0 ? `${fmt(ca / cmd)} €` : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {part != null ? `${part}%` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-gray-400 mt-4">{data?.meta?.canaux}</p>
          <p className="text-[11px] text-gray-400 mt-1">{SITE_WEB_SEMANTICS_HINT}</p>
        </div>

      <div
        className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
        data-testid="analytics-merchant-payment-pie"
      >
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-indigo-500" />
            Moyens de paiement
            <AnalyticsInfoHint text="Répartition des commandes payées selon le moyen de paiement utilisé." />
          </h3>
        </div>
        {(() => {
          const rows = (data?.paiements || []).filter((p) => Number(p.commandes || 0) > 0);
          const totalCmd = rows.reduce((s, p) => s + Number(p.commandes || 0), 0);
          const paidCmd = Number(kpi.commandes?.value ?? kpi.volume ?? 0);
          if (!rows.length) {
            return <p className="text-sm text-gray-500">Aucun paiement classé sur la période.</p>;
          }
          return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <div className="h-52">
                <Doughnut
                  key={`pay-${commercantId}-${periode}-${rows.map((p) => p.commandes).join('-')}`}
                  data={{
                    labels: rows.map((p) => p.label),
                    datasets: [
                      {
                        data: rows.map((p) => Number(p.commandes || 0)),
                        backgroundColor: CHART_COLORS.slice(2),
                        borderWidth: 2,
                        borderColor: '#fff',
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'bottom' } },
                  }}
                />
              </div>
              <ul className="space-y-2 text-sm text-gray-700">
                {rows.map((p) => {
                  const cmd = Number(p.commandes || 0);
                  const ca = Number(p.ca || 0);
                  const pct = totalCmd > 0 ? Math.round((cmd / totalCmd) * 1000) / 10 : 0;
                  return (
                    <li
                      key={p.key || p.label}
                      className="flex justify-between gap-3 border-b border-gray-50 pb-2"
                    >
                      <span className="font-medium text-gray-800">{p.label}</span>
                      <span className="tabular-nums text-right shrink-0">
                        {cmd} cmd · {fmt(ca)} € · {pct}%
                      </span>
                    </li>
                  );
                })}
                <li className="flex justify-between gap-3 pt-1 text-xs text-gray-500">
                  <span>Total camembert</span>
                  <span className="tabular-nums">
                    {totalCmd} cmd
                    {paidCmd > 0 ? ` · KPI commandes ${paidCmd}` : ''}
                  </span>
                </li>
                {paidCmd > 0 && totalCmd !== paidCmd ? (
                  <li
                    className="text-xs text-amber-800"
                    data-testid="analytics-merchant-payment-mismatch"
                  >
                    Attention : total paiements ({totalCmd}) ≠ commandes payées ({paidCmd}).
                  </li>
                ) : null}
              </ul>
            </div>
          );
        })()}
      </div>

      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            Évolution du CA net ventes
            <AnalyticsInfoHint text="CA net et nombre de commandes payées par jour sur la période." />
          </h3>
          <button
            type="button"
            onClick={() => setDetailKey('ca')}
            className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-900"
          >
            Voir le détail
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-100 max-h-72 overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-gray-500 sticky top-0">
              <tr>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium text-right">CA net</th>
                <th className="px-3 py-2 font-medium text-right">Commandes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(data?.evolution || []).map((jour) => (
                <tr key={jour.date}>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                    {new Date(`${jour.date}T12:00:00`).toLocaleDateString('fr-FR', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                    })}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    {fmt(jour.ca)} €
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                    {jour.volume ?? jour.commandes ?? 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(data?.topCategoriesCa || []).length > 0 ? (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            Performance par catégorie
            <AnalyticsInfoHint text="CA et quantités vendues regroupés par catégorie produit, sur les commandes payées de la période." />
          </h3>
          <p className="text-xs text-gray-500 mb-4">Familles qui font réellement votre activité</p>
          <div className="overflow-x-auto rounded-lg border border-gray-100">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Catégorie</th>
                  <th className="px-3 py-2 font-medium text-right">Quantité</th>
                  <th className="px-3 py-2 font-medium text-right">CA</th>
                  <th className="px-3 py-2 font-medium text-right">Part du CA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.topCategoriesCa.map((c) => (
                  <tr key={c.categorie}>
                    <td className="px-3 py-2 font-medium text-gray-800">{c.categorie}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{c.quantite}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(c.ca)} €</td>
                    <td className="px-3 py-2 text-right tabular-nums">{c.partCaPct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              Performance des produits
              <AnalyticsInfoHint text="Produits les plus vendeurs en CA ou en quantité, sur les commandes payées de la période." />
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Un seul tableau — triez les colonnes</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-500 uppercase tracking-wide">Trier par</span>
            <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-slate-50">
              <button
                type="button"
                onClick={() => setProductSort('ca')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                  productSort === 'ca'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                CA
              </button>
              <button
                type="button"
                onClick={() => setProductSort('quantite')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                  productSort === 'quantite'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                Quantité vendue
              </button>
              <button
                type="button"
                onClick={() => setProductSort('part')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md ${
                  productSort === 'part'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-800'
                }`}
              >
                Part du CA
              </button>
            </div>
            <button
              type="button"
              onClick={() => setDetailKey('produits')}
              className="inline-flex items-center gap-1 text-sm font-medium text-indigo-700 hover:text-indigo-900"
            >
              Voir le détail
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
        {(() => {
          const base =
            productSort === 'quantite'
              ? [...(data?.topProduitsQuantite || [])]
              : [...(data?.topProduitsCa || [])];
          const rows =
            productSort === 'part'
              ? base.sort((a, b) => Number(b.partCaPct || 0) - Number(a.partCaPct || 0))
              : base;
          if (!rows.length) {
            return <p className="text-sm text-gray-500">Aucune vente produit sur la période.</p>;
          }
          return (
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Produit</th>
                    <th className="px-3 py-2 font-medium">Catégorie</th>
                    <th className="px-3 py-2 font-medium text-right">Quantité vendue</th>
                    <th className="px-3 py-2 font-medium text-right">CA</th>
                    <th className="px-3 py-2 font-medium text-right">Part du CA</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((p) => (
                    <tr key={`${productSort}-${p.produitId}`}>
                      <td className="px-3 py-2 font-medium text-gray-900">{p.nom}</td>
                      <td className="px-3 py-2 text-gray-600">{p.categorie || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.quantite}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(p.ca)} €</td>
                      <td className="px-3 py-2 text-right tabular-nums">{p.partCaPct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })()}
        <p className="text-[11px] text-gray-400 mt-3">
          Analyse secondaire :{' '}
          <button
            type="button"
            className="underline hover:text-gray-700"
            onClick={() => setDetailKey('panier')}
          >
            Panier moyen
          </button>
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Clock className="h-5 w-5 text-slate-500" />
            Heures les plus actives
            <AnalyticsInfoHint text="Créneaux horaires où vous enregistrez le plus de commandes payées et de CA sur la période." />
          </h3>
          <p className="text-xs text-gray-500 mb-4">Quand vous vendez le plus — et ce que cela rapporte</p>
          {(data?.heuresPics || []).length === 0 ? (
            <p className="text-sm text-gray-500">Pas assez de ventes pour identifier un rush.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">Heure</th>
                    <th className="px-3 py-2 font-medium text-right">Commandes</th>
                    <th className="px-3 py-2 font-medium text-right">CA</th>
                    <th className="px-3 py-2 font-medium text-right">Panier moyen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(data?.heuresPics || []).map((h) => {
                    const cmd = Number(h.commandes || 0);
                    const ca = Number(h.ca || 0);
                    return (
                      <tr key={h.heure}>
                        <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">
                          {h.heure}h – {h.heure + 1}h
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{cmd}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmt(ca)} €</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {cmd > 0 ? `${fmt(ca / cmd)} €` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              Répartition par canal
              <AnalyticsInfoHint text="Aperçu des commandes payées par canal — même chiffres que Performance par canal." />
            </h3>
            <button
              type="button"
              onClick={() => setDetailKey('commandes')}
              className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-900"
            >
              Voir le détail
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {(() => {
            const rows = (data?.canaux || []).filter((c) => Number(c.commandes || 0) > 0);
            const total = rows.reduce((a, c) => a + Number(c.commandes || 0), 0);
            if (!rows.length) {
              return <p className="text-sm text-gray-500">Aucune commande payée sur la période.</p>;
            }
            const COLORS = ['bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500', 'bg-slate-500'];
            return (
              <div className="space-y-2">
                {rows.map((c, i) => {
                  const count = Number(c.commandes || 0);
                  const pct = total > 0 ? Math.round((count / total) * 1000) / 10 : 0;
                  return (
                    <div key={c.key || c.label} className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full shrink-0 ${COLORS[i % COLORS.length]}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between text-sm gap-2">
                          <span className="text-gray-800 truncate">{c.label}</span>
                          <span className="font-semibold tabular-nums shrink-0">
                            {count} · {pct}%
                          </span>
                        </div>
                        <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className={`h-full ${COLORS[i % COLORS.length]}`}
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>

      <p className="text-xs text-gray-400">{data?.meta?.reglesCa}</p>
    </div>
  );
};

export default CommercantAnalytics;
